import uuid from 'uuid';
import { fp as _ } from '@onsolve/onsolve-ui-components';

import LocalStorageUtility from 'common/utility/localStorageUtility';
import storageKeys from '@components/GlobalSearch/Utils/storageKeys'
import { tilesList } from '@components/GlobalSearch/Tiles/tilesList';

import searchTermsMap from '@components/GlobalSearch/Utils/searchTermsMap'


const MAX_HISTORY_LENGTH = 5;

class SidebarSearchService {
    constructor(menuItems, translations = {}, access, getFeature) {
        this.menuItems = [
            ...formatMenuItems(menuItems.slice(), translations),
            ...formatMenuTiles(_.cloneDeep(tilesList), translations, access, getFeature)
        ];

        this.relatedTerms = formatRelatedTerms(searchTermsMap, translations);
    }

    search = (query, withRelatedTerms) => {
        query = query.trim();

        if (!query) {
            return { resultsByTerms: [] };
        }
        let { searchTerms, relatedTerm } = getSearchTerms(query, this.relatedTerms, withRelatedTerms);
        const resultsByTerms = searchByTerms(searchTerms, this.menuItems);
        if (relatedTerm && !resultsByTerms.find(({ term }) => term === relatedTerm)) {
            relatedTerm = '';
        }

        return { resultsByTerms, relatedTerm };
    };
}

const formatRelatedTerms = (originalTerms, translations) => {
    return originalTerms.map(({ legacyTerm, newTerm }) => ({
        legacyTerm: translations.data[legacyTerm.id] || translations.data[legacyTerm.defaultMessage] || legacyTerm.defaultMessage || '',
        newTerm: translations.data[newTerm.id] || translations.data[newTerm.defaultMessage] ||  newTerm.defaultMessage || ''
    }));
};
const formatMenuTiles = (tilesList, translations, access, getFeature) => {
    return Object.entries(tilesList).reduce((acc, [parentPath, tiles]) => {
        let entry = tiles.reduce((acc, item) => {
            const { title: i18n, path, onClick, visible, parentFeature, parentVisible = () => true, icon } = item;
            const isParentVisible = parentVisible(access);
            const isParentFeatureEnabled = getFeature(parentFeature);
            const isItemVisible = visible ? visible(access) : true;
            if (!isParentVisible || !isParentFeatureEnabled || !isItemVisible) {
                return [...acc];
            }
            const translatedTitle = (i18n && translations.data[i18n.id]) || (i18n && translations.data[i18n.defaultMessage]) || i18n.defaultMessage;
            let formattedItem = {
                id: uuid(),
                translatedTitle,
                breadCrumb: translatedTitle,
                onClick,
                icon: icon
            };

            if (typeof path === 'object') {
                formattedItem = { ...formattedItem, ...path };
            } else {
                formattedItem.routeTo = parent && path && `${parentPath}${path}`;
            }

            return [...acc, formattedItem];
        }, []);

        return [...acc, ...entry];
    }, []);
};

const formatMenuItems = (menuItems, translations, parentBreadcrumb) => {
    return menuItems.reduce((acc, item) => {
        let childrenItems = [];
        const {
            id,
            children,
            linkTitle,
            i18n,
            routeTo,
            menuType,
            parent,
            isEnabled,
            redirectTo,
            redirectBaseUrl,
            newNav,
            icon
        } = item;

         const translatedTitle = (i18n && translations.data[i18n.id]) || (i18n && translations.data[i18n.defaultMessage]) || linkTitle;
        const breadCrumb = parentBreadcrumb ? parentBreadcrumb + '/' + translatedTitle : translatedTitle;

        if (children) {
            childrenItems = formatMenuItems(children, translations, breadCrumb);
        }

        return [
            ...acc,
            ...childrenItems,
            {
                id,
                parent,
                routeTo,
                menuType,
                isEnabled,
                breadCrumb,
                translatedTitle,
                redirectTo,
                redirectBaseUrl,
                newNav,
                icon
            }
        ];
    }, []);
};

const findByTerm = term => item => item.translatedTitle.toLowerCase().includes(term.toLowerCase());

const getSearchTerms = (query, relatedTerms, withRelatedTerms) => {
    query = query
        .split(' ')
        .filter(Boolean)
        .map(word => word?.charAt(0)?.toUpperCase() + word?.toLowerCase()?.slice(1))
        .join(' ');

    const searchTerms = _.uniq(query.split(' ').sort());

    if (searchTerms.length > 1) {
        searchTerms.unshift(query);
    }

    let relatedTerm = '';

    if (withRelatedTerms) {
        relatedTerm = getRelatedTerm(query, relatedTerms) || '';

        if (relatedTerm) {
            searchTerms.unshift(relatedTerm);
        }
    }

    return { searchTerms, relatedTerm };
};

const searchByTerms = (searchTerms, menuItems) => {
    let filteredMenuItems = menuItems.slice();
    const searchResults = [];
    searchTerms.forEach(term => {
        const resultsByTerm = _.sortBy(filteredMenuItems.filter(findByTerm(term)), item => item.translatedTitle);

        if (resultsByTerm.length) {
            searchResults.push({ term, results: resultsByTerm });
            filteredMenuItems = _.difference(filteredMenuItems, resultsByTerm);
        }
    });

    return searchResults;
};

const getRelatedTerm = (query, relatedTerms) =>
    _.get(relatedTerms.find(({ legacyTerm }) => legacyTerm.toLowerCase().includes(query.toLowerCase())), 'newTerm', '');

const getRecentSearchHistory = userId => {
    const recentSearch = LocalStorageUtility.getData(storageKeys.recentSearch);

    return recentSearch?.[userId] ? recentSearch[userId] : [];
};

const removeOneSearchFromHistory = (userId, value) => {
    const recentSearch = LocalStorageUtility.getData(storageKeys.recentSearch);

    if (recentSearch[userId].includes(value)) {
        recentSearch[userId].splice(recentSearch[userId].indexOf(value), 1);
    }

    LocalStorageUtility.setData(storageKeys.recentSearch, recentSearch);
};

const addNewSearchToHistory = (userId, value) => {
    let recentSearch = LocalStorageUtility.getData(storageKeys.recentSearch) || {};

    if (recentSearch[userId]) {
        if (recentSearch[userId].includes(value)) {
            recentSearch[userId].splice(recentSearch[userId].indexOf(value), 1);
        }
        recentSearch[userId].unshift(value);
    } else {
        recentSearch[userId] = [value];
    }

    if (recentSearch[userId].length > MAX_HISTORY_LENGTH) {
        recentSearch[userId].length = MAX_HISTORY_LENGTH;
    }

    LocalStorageUtility.setData(storageKeys.recentSearch, recentSearch);
};

const clearRecentSearchesHistory = userId => {
    const recentSearch = LocalStorageUtility.getData(storageKeys.recentSearch);

    delete recentSearch[userId];
    LocalStorageUtility.setData(storageKeys.recentSearch, recentSearch);
};

const addSpaceToBreadCrumb = origin => origin.replace(/\//g, ' / ');

export {
    SidebarSearchService,
    getRecentSearchHistory,
    addNewSearchToHistory,
    clearRecentSearchesHistory,
    removeOneSearchFromHistory,
    addSpaceToBreadCrumb
};
